import { useState, useEffect, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { Box, Chip, Tabs, Tab } from '@mui/material'
import {
  Add,
  Edit,
  Delete,
  Print,
  CheckCircle,
  Send,
  Receipt,
} from '@mui/icons-material'
import { PageHeader, Button, DataTable, Card, Modal, PermissionGate, ExportButtons } from '@components/common'
import { IssueVoucherForm } from '@components/inventory'
import { usePermissions } from '@context/PermissionContext'
import { toast } from 'react-toastify'
import api from '@services/api'
import { formatDate } from '@utils/dateUtils'

const STATUS_OPTIONS = [
  { value: '', label: 'All Vouchers' },
  { value: 'draft', label: 'Draft' },
  { value: 'pending', label: 'Pending' },
  { value: 'approved', label: 'Approved' },
  { value: 'issued', label: 'Issued' },
  { value: 'received', label: 'Received' },
  { value: 'cancelled', label: 'Cancelled' },
]

const getStatusColor = (status) => {
  const colors = {
    draft: 'default',
    pending: 'warning',
    approved: 'info',
    issued: 'primary',
    received: 'success',
    cancelled: 'error',
  }
  return colors[status] || 'default'
}

const columns = [
  {
    field: 'sn',
    headerName: 'SN',
    width: 50,
    renderCell: (_, row, index) => index + 1,
  },
  {
    field: 'voucher_number',
    headerName: 'Voucher #',
    sortable: true,
    width: 120,
  },
  {
    field: 'voucher_date',
    headerName: 'Date',
    sortable: true,
    width: 100,
    renderCell: (v) => formatDate(v) || '-',
  },
  {
    field: 'to_department_name',
    headerName: 'To Section/Unit',
    sortable: true,
    renderCell: (v) => v || '-',
  },
  {
    field: 'unit_ward',
    headerName: 'Unit/Ward',
    sortable: true,
    renderCell: (v) => v || '-',
  },
  {
    field: 'item_count',
    headerName: 'Items',
    width: 70,
    align: 'center',
    renderCell: (v) => v || 0,
  },
  {
    field: 'total_amount',
    headerName: 'Total Amount',
    sortable: true,
    align: 'right',
    renderCell: (v) => (v ? `UGX ${Number(v).toLocaleString()}` : '-'),
  },
  {
    field: 'status',
    headerName: 'Status',
    width: 110,
    renderCell: (v, row) => (
      <Chip
        label={row.status_display || v}
        size="small"
        color={getStatusColor(v)}
      />
    ),
  },
  {
    field: 'issued_by_name',
    headerName: 'Issued By',
    sortable: true,
    renderCell: (v) => v || '-',
  },
  {
    field: 'received_by_display',
    headerName: 'Received By',
    sortable: true,
    renderCell: (v) => v || '-',
  },
]

const IssueVouchersList = () => {
  const navigate = useNavigate()
  const [vouchers, setVouchers] = useState([])
  const [loading, setLoading] = useState(true)
  const [formOpen, setFormOpen] = useState(false)
  const [selectedVoucher, setSelectedVoucher] = useState(null)
  const [deleteModalOpen, setDeleteModalOpen] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [statusFilter, setStatusFilter] = useState('')
  const [issueModalOpen, setIssueModalOpen] = useState(false)
  const [receiveModalOpen, setReceiveModalOpen] = useState(false)
  const [actionLoading, setActionLoading] = useState(false)
  const { canCreate, canEdit, canDelete, hasPermission } = usePermissions()

  // Export columns
  const exportColumns = useMemo(() => [
    { field: 'voucher_number', headerName: 'Voucher #' },
    { field: 'voucher_date', headerName: 'Date', renderValue: (v) => v ? formatDate(v) : '' },
    { field: 'to_department_name', headerName: 'To Section/Unit' },
    { field: 'unit_ward', headerName: 'Unit/Ward' },
    { field: 'item_count', headerName: 'Items' },
    { field: 'total_amount', headerName: 'Total Amount', renderValue: (v) => v ? `UGX ${Number(v).toLocaleString()}` : '' },
    { field: 'status', headerName: 'Status', renderValue: (v) => STATUS_OPTIONS.find(s => s.value === v)?.label || v },
    { field: 'issued_by_name', headerName: 'Issued By' },
    { field: 'received_by_display', headerName: 'Received By' },
  ], [])

  const getActiveFilterLabels = () => {
    const labels = {}
    if (statusFilter) labels.Status = STATUS_OPTIONS.find(s => s.value === statusFilter)?.label || statusFilter
    return labels
  }

  const fetchVouchers = async () => {
    try {
      setLoading(true)
      const params = statusFilter ? { status: statusFilter } : {}
      const response = await api.get('/inventory/issue-vouchers/', { params })
      setVouchers(response.data.results || response.data)
    } catch (error) {
      console.error('Error fetching vouchers:', error)
      toast.error('Failed to load issue vouchers')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchVouchers()
  }, [statusFilter])

  const handleFormSuccess = () => {
    fetchVouchers()
    setSelectedVoucher(null)
  }

  const handleEdit = async (voucher) => {
    try {
      // Fetch full voucher details including line items
      const response = await api.get(`/inventory/issue-vouchers/${voucher.id}/`)
      setSelectedVoucher(response.data)
      setFormOpen(true)
    } catch (error) {
      console.error('Error fetching voucher details:', error)
      toast.error('Failed to load voucher details')
    }
  }

  const handleDelete = async () => {
    if (!selectedVoucher) return
    setDeleting(true)
    try {
      await api.delete(`/inventory/issue-vouchers/${selectedVoucher.id}/`)
      toast.success('Voucher deleted successfully')
      fetchVouchers()
      setDeleteModalOpen(false)
      setSelectedVoucher(null)
    } catch (error) {
      console.error('Error deleting voucher:', error)
      toast.error('Failed to delete voucher')
    } finally {
      setDeleting(false)
    }
  }

  const handleIssue = async () => {
    if (!selectedVoucher) return
    setActionLoading(true)
    try {
      await api.post(`/inventory/issue-vouchers/${selectedVoucher.id}/issue/`)
      toast.success('Voucher issued successfully. Stock has been deducted.')
      fetchVouchers()
      setIssueModalOpen(false)
      setSelectedVoucher(null)
    } catch (error) {
      console.error('Error issuing voucher:', error)
      toast.error(error.response?.data?.error || 'Failed to issue voucher')
    } finally {
      setActionLoading(false)
    }
  }

  const handleReceive = async () => {
    if (!selectedVoucher) return
    setActionLoading(true)
    try {
      await api.post(`/inventory/issue-vouchers/${selectedVoucher.id}/receive/`)
      toast.success('Voucher marked as received')
      fetchVouchers()
      setReceiveModalOpen(false)
      setSelectedVoucher(null)
    } catch (error) {
      console.error('Error receiving voucher:', error)
      toast.error(error.response?.data?.error || 'Failed to mark as received')
    } finally {
      setActionLoading(false)
    }
  }

  const handlePrint = (voucher) => {
    navigate(`/inventory/issue-vouchers/${voucher.id}/print`)
  }

  const actionsColumn = {
    field: 'actions',
    headerName: '',
    width: 180,
    renderCell: (_, row) => (
      <Box display="flex" gap={0.5}>
        <Button
          size="small"
          variant="text"
          onClick={(e) => {
            e.stopPropagation()
            handlePrint(row)
          }}
          title="Print"
        >
          <Print fontSize="small" />
        </Button>
        {canEdit('issue_vouchers') && ['draft', 'pending'].includes(row.status) && (
          <Button
            size="small"
            variant="text"
            onClick={(e) => {
              e.stopPropagation()
              handleEdit(row)
            }}
            title="Edit"
          >
            <Edit fontSize="small" />
          </Button>
        )}
        {hasPermission('issue_vouchers.approve') && ['draft', 'pending', 'approved'].includes(row.status) && (
          <Button
            size="small"
            variant="text"
            color="primary"
            onClick={(e) => {
              e.stopPropagation()
              setSelectedVoucher(row)
              setIssueModalOpen(true)
            }}
            title="Issue"
          >
            <Send fontSize="small" />
          </Button>
        )}
        {hasPermission('issue_vouchers.approve') && row.status === 'issued' && (
          <Button
            size="small"
            variant="text"
            color="success"
            onClick={(e) => {
              e.stopPropagation()
              setSelectedVoucher(row)
              setReceiveModalOpen(true)
            }}
            title="Mark Received"
          >
            <CheckCircle fontSize="small" />
          </Button>
        )}
        {canDelete('issue_vouchers') && row.status === 'draft' && (
          <Button
            size="small"
            variant="text"
            color="error"
            onClick={(e) => {
              e.stopPropagation()
              setSelectedVoucher(row)
              setDeleteModalOpen(true)
            }}
            title="Delete"
          >
            <Delete fontSize="small" />
          </Button>
        )}
      </Box>
    ),
  }

  return (
    <Box>
      <PageHeader
        title="Issue Vouchers"
        subtitle="Manage stock issue vouchers for sections/units"
        breadcrumbs={[
          { label: 'Dashboard', path: '/dashboard' },
          { label: 'Stock', path: '/inventory' },
          { label: 'Issue Vouchers' },
        ]}
        actions={
          <>
            <ExportButtons
              data={vouchers}
              columns={exportColumns}
              title="Issue Vouchers List"
              filename="issue_vouchers"
              filters={getActiveFilterLabels()}
            />
            <PermissionGate permission="issue_vouchers.create">
              <Button startIcon={<Add />} onClick={() => setFormOpen(true)}>
                New Voucher
              </Button>
            </PermissionGate>
          </>
        }
      />

      <Card>
        <Tabs
          value={statusFilter}
          onChange={(_, v) => setStatusFilter(v)}
          sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
        >
          {STATUS_OPTIONS.map((opt) => (
            <Tab
              key={opt.value}
              value={opt.value}
              label={opt.label}
              sx={{ minHeight: 48 }}
            />
          ))}
        </Tabs>

        <DataTable
          columns={[...columns, actionsColumn]}
          data={vouchers}
          loading={loading}
          searchPlaceholder="Search vouchers..."
          emptyMessage="No issue vouchers found"
          onRowClick={(row) => handlePrint(row)}
        />
      </Card>

      <IssueVoucherForm
        open={formOpen}
        onClose={() => {
          setFormOpen(false)
          setSelectedVoucher(null)
        }}
        voucher={selectedVoucher}
        onSuccess={handleFormSuccess}
      />

      {/* Delete Confirmation */}
      <Modal
        open={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        title="Delete Voucher"
        maxWidth="xs"
        actions={
          <>
            <Button variant="outlined" onClick={() => setDeleteModalOpen(false)} disabled={deleting}>
              Cancel
            </Button>
            <Button color="error" onClick={handleDelete} loading={deleting}>
              Delete
            </Button>
          </>
        }
      >
        Are you sure you want to delete voucher "{selectedVoucher?.voucher_number}"?
      </Modal>

      {/* Issue Confirmation */}
      <Modal
        open={issueModalOpen}
        onClose={() => setIssueModalOpen(false)}
        title="Issue Voucher"
        maxWidth="sm"
        actions={
          <>
            <Button variant="outlined" onClick={() => setIssueModalOpen(false)} disabled={actionLoading}>
              Cancel
            </Button>
            <Button onClick={handleIssue} loading={actionLoading} startIcon={<Send />}>
              Issue Voucher
            </Button>
          </>
        }
      >
        <Box>
          Are you sure you want to issue voucher "{selectedVoucher?.voucher_number}"?
          <Box sx={{ mt: 2, p: 2, bgcolor: 'warning.lighter', borderRadius: 1 }}>
            <strong>Note:</strong> This will deduct the items from inventory stock.
          </Box>
        </Box>
      </Modal>

      {/* Receive Confirmation */}
      <Modal
        open={receiveModalOpen}
        onClose={() => setReceiveModalOpen(false)}
        title="Mark as Received"
        maxWidth="sm"
        actions={
          <>
            <Button variant="outlined" onClick={() => setReceiveModalOpen(false)} disabled={actionLoading}>
              Cancel
            </Button>
            <Button color="success" onClick={handleReceive} loading={actionLoading} startIcon={<CheckCircle />}>
              Mark Received
            </Button>
          </>
        }
      >
        Mark voucher "{selectedVoucher?.voucher_number}" as received by the department?
      </Modal>
    </Box>
  )
}

export default IssueVouchersList
